import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CurrencyNotice } from "@/components/domain/CurrencyNotice";
import {
  ArrowRight,
  Boxes,
  FileText,
  FlaskConical,
  Wallet,
} from "lucide-react";
import { PageBody } from "@/components/ui/PageHeader";
import { Panel, PanelHeader } from "@/components/ui/Panel";
import { KpiTile } from "@/components/domain/KpiTile";
import { InventoryExceptions } from "@/components/domain/InventoryExceptions";
import { BarMeter } from "@/components/charts/BarMeter";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { useDashboard } from "@/api/hooks";
import { formatCompactMoney, formatMoney } from "@/domain/money";
import { timeAgo } from "@/domain/datetime";
import { translateStatus } from "@/domain/i18n";

const ACTIVITY_TONE_VAR: Record<string, string> = {
  brand: "var(--color-brand)",
  ok: "var(--color-ok)",
  warn: "var(--color-warn)",
  info: "var(--color-info)",
  brass: "var(--color-brass)",
  neutral: "var(--color-ink-faint)",
};

/**
 * Thay đổi doanh thu so với tháng trước — TÍNH THẬT.
 *
 * Trước đây chỗ này in cứng "▲ 12.4% vs last month" ngay trong mã, không đọc
 * một con số nào từ dữ liệu. Một phần trăm sai trên trang chủ nguy hiểm hơn là
 * không có phần trăm nào: lãnh đạo nhìn nó để ra quyết định.
 *
 * Tháng trước bằng 0 thì KHÔNG in "+∞%" hay "+100%" — không có gì để so, nói
 * thẳng là không so được.
 */
function MonthOverMonth({ now, prev }: { now: number; prev: number }) {
  const { t } = useTranslation();
  if (prev <= 0) {
    return <span className="text-ink-soft">{t("dashboard.noPrevMonth")}</span>;
  }
  const pct = ((now - prev) / prev) * 100;
  const up = pct >= 0;
  return (
    <span className={up ? "text-ok" : "text-danger"}>
      {up ? "▲" : "▼"} {t("dashboard.changeVsLastMonth", { value: Math.abs(pct).toFixed(1) })}
    </span>
  );
}

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const { data: d, isLoading, error, refetch } = useDashboard();

  if (isLoading) {
    return (
      <PageBody>
        <LoadingState />
      </PageBody>
    );
  }
  if (error || !d) {
    return (
      <PageBody>
        <ErrorState message={t("dashboard.errorLoad")} onRetry={() => refetch()} />
      </PageBody>
    );
  }

  return (
    <PageBody>
      <CurrencyNotice excluded={d.excludedCurrencies} />

      {/* KPI row */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile
          label={t("dashboard.revenueMTD")}
          value={formatCompactMoney(d.revenueMTD)}
          icon={<Wallet />}
          spark={d.revenueSpark}
          sub={<MonthOverMonth now={d.revenueMTD} prev={d.revenuePrevMonth} />}
        />
        <KpiTile
          label={t("dashboard.openPis")}
          value={String(d.openPis)}
          icon={<FileText />}
          sub={<span className="text-ink-soft">{t("dashboard.awaiting")}</span>}
        />
        <KpiTile
          label={t("dashboard.arOutstanding")}
          value={formatCompactMoney(d.arOutstanding)}
          icon={<Boxes />}
          sub={
            <span className="text-danger">
              {formatMoney(d.arOverdue)} {t("dashboard.arOverdue")}
            </span>
          }
        />
        <KpiTile
          label={t("dashboard.samples")}
          value={String(d.samplesInProgress)}
          icon={<FlaskConical />}
          sub={
            /* 0 cảnh báo có thể nghĩa là "mọi thứ ổn" HOẶC "chưa ai đặt ngưỡng
               cho sản phẩm nào" — hai chuyện khác hẳn nhau, phải nói rõ. */
            d.lowStock > 0 ? (
              <span className="text-warn">
                {d.lowStock} {t("dashboard.lowStock").toLowerCase()}
              </span>
            ) : (
              <span className="text-ink-faint">
                {t("dashboard.noReorderPoint", { count: d.lowStockWithoutThreshold })}
              </span>
            )
          }
        />
      </div>

      {/* Việc còn tồn — bấm thẳng vào module. Hệ cũ đếm "tổng số hoá đơn / tổng
          số PO", những con số chỉ lớn dần và không nói ai phải làm gì hôm nay.
          Dòng nào bằng 0 đã bị máy chủ loại, nên ở đây không có ô rỗng. */}
      {d.worklist.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {d.worklist.map((w) => (
            <Link
              key={w.key}
              to={w.to}
              className="group flex items-center gap-2 rounded-sm border border-line bg-surface px-3 py-1.5 text-xs text-ink-soft transition-colors hover:border-brand hover:text-ink"
            >
              <span className="font-mono tabular text-sm font-semibold text-brand">{w.count}</span>
              {t(`dashboard.work.${w.key}`)}
              <ArrowRight className="size-3 opacity-0 transition-opacity group-hover:opacity-100" />
            </Link>
          ))}
        </div>
      )}

      <InventoryExceptions data={d.inventoryExceptions} />

      {/* Nhịp lập chứng từ 7 ngày. Cột chồng như hệ cũ, nhưng gom nhóm theo
          múi giờ công ty ở SQL chứ không cắt chuỗi ISO (cắt ISO là gom theo
          UTC, nên chứng từ lập lúc 8 giờ tối rơi sang ngày hôm sau). */}
      <Panel className="mt-3">
        <PanelHeader title={t("dashboard.activity7d")} />
        <div className="flex items-end gap-1.5 px-4 pb-4 pt-1">
          {d.activity7d.map((a) => {
            const total = a.pis + a.invoices + a.purchaseOrders;
            const max = Math.max(1, ...d.activity7d.map((x) => x.pis + x.invoices + x.purchaseOrders));
            const h = (n: number) => `${(n / max) * 56}px`;
            return (
              <div key={a.day} className="flex flex-1 flex-col items-center gap-1">
                <span className="font-mono tabular text-2xs text-ink-faint">{total || ""}</span>
                <div
                  className="flex w-full flex-col-reverse overflow-hidden rounded-sm bg-sunken"
                  style={{ height: "56px" }}
                  title={`${a.day} · ${t("nav.proformaInvoices")} ${a.pis} · ${t("nav.invoices")} ${a.invoices} · ${t("nav.purchaseOrders")} ${a.purchaseOrders}`}
                >
                  <div style={{ height: h(a.pis) }} className="w-full bg-brand" />
                  <div style={{ height: h(a.invoices) }} className="w-full bg-[color:var(--color-brass)]" />
                  <div style={{ height: h(a.purchaseOrders) }} className="w-full bg-ink-faint" />
                </div>
                <span className="text-2xs text-ink-faint">{a.day.slice(5)}</span>
              </div>
            );
          })}
        </div>
      </Panel>

      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
        {/* Recent activity — spans 2 */}
        <Panel className="lg:col-span-2">
          <PanelHeader
            title={t("dashboard.recentActivity")}
            actions={
              <Link to="/proforma-invoices" className="text-xs font-medium text-brand hover:underline">
                {t("common.viewAll")}
              </Link>
            }
          />
          <ul className="divide-y divide-line">
            {d.recentActivity.map((a) => (
              <li key={a.id} className="flex items-center gap-3 px-4 py-2.5">
                <span
                  className="grid size-8 shrink-0 place-items-center rounded-sm"
                  style={{ background: `color-mix(in srgb, ${ACTIVITY_TONE_VAR[a.tone]} 12%, transparent)` }}
                >
                  <span
                    className="size-2 rounded-full"
                    style={{ background: ACTIVITY_TONE_VAR[a.tone] }}
                  />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-ink">
                    <span className="font-medium">{a.who}</span>{" "}
                    <span className="text-ink-soft">
                      {a.kind === "payment_recorded"
                        ? t("dashboard.paymentActivity", {
                            method: a.method ?? t("dashboard.paymentMethod"),
                            amount: formatMoney(a.amount, a.currency),
                          })
                        : t(`dashboard.activity.${a.kind}`, {
                            amount: formatMoney(a.amount, a.currency),
                          })}
                    </span>
                  </p>
                  {/* Bấm được vào đúng chứng từ. Trước đây người dùng thấy
                      "PI 000378" rồi phải tự sang danh sách gõ lại số. */}
                  {a.href ? (
                    <Link to={a.href} className="font-mono tabular text-2xs text-brand hover:underline">
                      {a.ref}
                    </Link>
                  ) : (
                    <p className="font-mono tabular text-2xs text-ink-faint">{a.ref}</p>
                  )}
                </div>
                <span className="shrink-0 text-xs text-ink-faint">{timeAgo(a.when, i18n.language)}</span>
              </li>
            ))}
          </ul>
        </Panel>

        {/* A/R aging + top customers */}
        <div className="flex flex-col gap-3">
          <Panel>
            <PanelHeader
              title={t("dashboard.arAging")}
              actions={
                <span className="font-mono tabular text-xs text-ink-soft">
                  {formatMoney(d.arOutstanding)}
                </span>
              }
            />
            <div className="px-4 py-3.5">
              <BarMeter
                rows={d.arAging.map((b, i) => ({
                  label: translateStatus(t, b.bucket),
                  value: b.amount,
                  display: formatCompactMoney(b.amount),
                  tone:
                    i >= 3
                      ? "var(--color-danger)"
                      : i === 2
                        ? "var(--color-warn)"
                        : "var(--color-brand)",
                }))}
              />
            </div>
          </Panel>

          <Panel>
            <PanelHeader title={t("dashboard.topCustomers")} />
            <div className="px-4 py-3.5">
              <BarMeter
                rows={d.topCustomers.map((c) => ({
                  label: c.name,
                  value: c.amount,
                  display: formatCompactMoney(c.amount),
                  tone: "var(--color-brass)",
                }))}
              />
            </div>
          </Panel>
        </div>
      </div>

    </PageBody>
  );
}
